import type { ConversationListItem } from '@/features/conversations/api';
import { useResolvedRoutes } from '@/lib/use-routes';
import { cn } from '@/lib/utils';
import { Link } from '@tanstack/react-router';
import { differenceInCalendarDays, format } from 'date-fns';
import type { ReactNode } from 'react';

import { ConversationHandlerBadge } from './conversation-handler-badge';
import { ConversationPlatformAvatar } from './conversation-platform-avatar';
import {
  openTeamFollowUps,
  teamFollowUpLabel,
} from './conversation-thread-events';

/**
 * The row's timestamp. The list has no day separators, so the row itself says
 * when: the time today, then "Yesterday", the weekday within a week, and a
 * date beyond that (the year only once it is not this year's).
 */
export function formatInboxTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  const days = differenceInCalendarDays(now, date);
  if (days <= 0) return format(date, 'h:mm a');
  if (days === 1) return 'Yesterday';
  if (days < 7) return format(date, 'EEEE');
  if (date.getFullYear() === now.getFullYear()) return format(date, 'd MMM');
  return format(date, 'd MMM yyyy');
}

interface ConversationInboxRowProps {
  conversation: ConversationListItem;
  isSelected: boolean;
  memberFirstNameById: Map<string, string>;
  variant?: 'sidebar' | 'mobile' | 'card';
  /**
   * When provided, selecting the row calls this instead of navigating to
   * `/dashboard/conversations` — used by the admin panel, which controls
   * selection locally rather than via the route.
   */
  onSelect?: (id: string) => void;
  /**
   * Desktop list only: a leading selection checkbox and a trailing actions
   * menu. With either, the row becomes a container with the link in the
   * middle — a checkbox or menu nested INSIDE the link would be invalid
   * markup and every click on them would also open the conversation.
   */
  leading?: ReactNode;
  trailing?: ReactNode;
}

export function ConversationInboxRow({
  conversation,
  isSelected,
  memberFirstNameById,
  variant = 'mobile',
  onSelect,
  leading,
  trailing,
}: ConversationInboxRowProps) {
  const routes = useResolvedRoutes();
  const displayName =
    conversation.externalUserName ?? conversation.externalUserId;
  const isUnread = conversation.isUnread;
  const isMobile = variant === 'mobile';
  const isCard = variant === 'card';
  const rowLabel = `${isUnread ? 'Unread: ' : ''}Open conversation with ${displayName}`;
  // Claire told the customer the team will send a price or details.
  const followUps = openTeamFollowUps(
    conversation,
    conversation.lastAgentMessageAt
  );

  const rowClassName = cn(
    'flex w-full items-start gap-3 px-4 py-4 text-left transition-colors',
    isMobile
      ? 'border-b border-[#F2F2F7] hover:bg-[#FAFAFA] active:bg-[#F2F2F7]'
      : isCard
        ? // A flat divided row inside the inbox card (Web A), not a floating pill.
          'border-b border-border/60 px-4 py-3.5 hover:bg-sidebar-accent'
        : 'rounded-xl bg-sidebar px-3 py-3 hover:bg-sidebar-accent',
    // Unread is carried by bold text, the blue time and the dot. On the desktop
    // list only the selected row gets a background: a tint on unread rows read
    // as "selected". The phone list has no selection, so it keeps the tint.
    isUnread && !isSelected && isMobile && 'bg-[#EEF3FF]',
    isSelected &&
      (isMobile ? 'bg-[#F2F2F7]' : 'bg-sidebar-accent hover:bg-sidebar-accent')
  );

  const rowContent = (
    <>
      <ConversationPlatformAvatar
        name={displayName}
        avatarUrl={conversation.externalUserAvatar}
        platform={conversation.platform}
        transport={conversation.transport}
      />

      <div className="min-w-0 flex-1">
        {/*
          THE NAME OWNS THE FIRST LINE.

          The handler badge used to sit beside it, and in a list column this
          narrow the two could not both fit: the badge takes its natural width
          and the name — the only `truncate` element — absorbs all of the
          shrinking. An "Unassigned" badge left about seven characters, so the
          list read "Ja…", "Em…", "Chl…" and you could not tell who a
          conversation was with. The badge's own width decided how much of the
          customer's name you got, which is exactly backwards: the name is what
          you scan the inbox by.

          The badge moves to the second line beside the message preview, where
          it is still visible but competes only with a string that is expected
          to be clipped.
        */}
        <div className="flex items-start justify-between gap-3">
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-[15px] leading-5',
              isUnread ? 'font-bold' : 'font-medium',
              isUnread ? 'text-black' : 'text-[#3C3C43]'
            )}
          >
            {displayName}
          </span>
          <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
            {conversation.lastMessageAt ? (
              <span
                className={cn(
                  'whitespace-nowrap text-[13px] leading-4',
                  isUnread ? 'font-semibold text-[#2E65F3]' : 'text-[#8E8E93]'
                )}
              >
                {formatInboxTime(conversation.lastMessageAt)}
              </span>
            ) : null}
            {isUnread ? (
              <span
                className={cn('size-2.5 shrink-0 rounded-full bg-[#2E65F3]')}
                role="img"
                aria-label="Unread"
              />
            ) : null}
          </div>
        </div>

        <div className="mt-1 flex items-center gap-1.5">
          <ConversationHandlerBadge
            conversation={conversation}
            memberFirstNameById={memberFirstNameById}
            className={cn('shrink-0')}
          />
          {followUps.length > 0 ? (
            <span
              className="max-w-[60%] shrink-0 truncate rounded-md bg-amber-100 px-1.5 py-0.5 font-medium text-[11px] text-amber-900 dark:bg-amber-950 dark:text-amber-200"
              title={followUps.map(teamFollowUpLabel).join(', ')}
            >
              {followUps.length === 1
                ? teamFollowUpLabel(followUps[0])
                : `Needs ${followUps.length} answers`}
            </span>
          ) : null}
          {conversation.isArchived ? (
            <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 font-medium text-[11px] text-muted-foreground">
              Archived
            </span>
          ) : null}
          {conversation.lastMessageContent ? (
            <p
              className={cn(
                'min-w-0 flex-1 truncate text-[14px] leading-5',
                isUnread ? 'font-medium text-black' : 'text-[#8E8E93]'
              )}
            >
              {conversation.lastMessageContent}
            </p>
          ) : null}
        </div>
      </div>
    </>
  );

  if (leading || trailing) {
    return (
      <div
        role="listitem"
        // Named like the plain row (a link with role=listitem), so the row is
        // found by the same name whether or not it carries a checkbox and menu.
        aria-label={rowLabel}
        className={cn(
          'group flex w-full items-start border-b border-border/60 transition-colors hover:bg-sidebar-accent',
          isSelected && 'bg-sidebar-accent'
        )}
      >
        {leading ? (
          <div className="shrink-0 pt-[26px] pl-4">{leading}</div>
        ) : null}
        {onSelect ? (
          <button
            type="button"
            onClick={() => onSelect(conversation.id)}
            className="flex min-w-0 flex-1 items-start gap-3 px-3 py-3.5 text-left"
            aria-label={rowLabel}
          >
            {rowContent}
          </button>
        ) : (
          <Link
            to={routes.conversations}
            // Keep the open view (`tab`) when opening a thread.
            search={(prev) => ({ ...prev, id: conversation.id })}
            className="flex min-w-0 flex-1 items-start gap-3 px-3 py-3.5 text-left"
            aria-label={rowLabel}
          >
            {rowContent}
          </Link>
        )}
        {trailing ? <div className="shrink-0 pt-5 pr-2">{trailing}</div> : null}
      </div>
    );
  }

  if (onSelect) {
    return (
      <button
        type="button"
        onClick={() => onSelect(conversation.id)}
        className={rowClassName}
        aria-label={rowLabel}
      >
        {rowContent}
      </button>
    );
  }

  return (
    <Link
      to={routes.conversations}
      // Keep the open view (`tab`) when opening a thread.
      search={(prev) => ({ ...prev, id: conversation.id })}
      className={rowClassName}
      role="listitem"
      aria-label={rowLabel}
    >
      {rowContent}
    </Link>
  );
}
